import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CalendarRange } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { MonthlyEnrollmentTable } from "@/components/admin/MonthlyEnrollmentTable";
import { MonthlyApprovalRates } from "@/components/admin/MonthlyApprovalRates";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/admin/monthly-enrollments")({
  head: () => ({
    meta: [
      { title: "Enrollments & Approvals - WeLivdIt Admin" },
      {
        name: "description",
        content: "Track unique student enrollments, revenue breakdown, and month-wise visa approval rates.",
      },
      { property: "og:title", content: "Enrollments & Approvals - WeLivdIt Admin" },
      {
        property: "og:description",
        content: "Track unique student enrollments, revenue breakdown, and month-wise visa approval rates.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MonthlyEnrollmentsPage,
});

function MonthlyEnrollmentsPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<"loading" | "ok" | "denied">("loading");

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/monthly-enrollments" } });
        return;
      }
      setGate((await hasAdminAccess(sess.session)) ? "ok" : "denied");
    })();
  }, [navigate]);

  if (gate === "denied")
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <p className="text-sm text-muted-foreground">You don't have access to this page.</p>
      </div>
    );

  return (
    <AdminShell
      title="Enrollments & Approvals"
      description="Track unique student enrollments, revenue breakdown, and month-wise visa approval rates."
      actions={
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground">
          <CalendarRange className="h-3.5 w-3.5 text-primary" />
          <span>Unique student reporting</span>
        </div>
      }
    >
      <Tabs defaultValue="enrollments" className="space-y-4">
        <div className="overflow-x-auto pb-1">
          <TabsList className="h-auto min-w-max">
            <TabsTrigger value="enrollments" className="min-h-8 px-3 text-xs sm:text-sm">
              Monthly Enrollments & Revenue
            </TabsTrigger>
            <TabsTrigger value="approvals" className="min-h-8 px-3 text-xs sm:text-sm">
              Monthly Approval Rates
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="enrollments" className="mt-0">
          <MonthlyEnrollmentTable />
        </TabsContent>
        <TabsContent value="approvals" className="mt-0">
          <MonthlyApprovalRates />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
